import React, { useState, useEffect } from "react";
import { useApi } from "../../hooks/useApi";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../../components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Calendar } from "../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { toast } from "sonner";
import {
    Plus,
    Clock,
    Calendar as CalendarIcon,
    Trash2,
    BarChart3,
} from "lucide-react";
import { cn, formatDate } from "../../lib/utils";
import { format } from "date-fns";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from "recharts";

export const TimeTrackingPage = () => {
    const { get, post, del } = useApi();
    const [entries, setEntries] = useState([]);
    const [projects, setProjects] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [selectedProject, setSelectedProject] = useState("");
    const [formData, setFormData] = useState({
        task_id: "",
        description: "",
        hours: "",
        date: new Date(),
    });

    const fetchData = async () => {
        const results = await Promise.allSettled([
            get("/time-entries"),
            get("/projects"),
            get("/time-entries/summary"),
        ]);
        if (results[0].status === "fulfilled") setEntries(results[0].value);
        else toast.error("Erreur lors du chargement des entrées de temps");
        if (results[1].status === "fulfilled") setProjects(results[1].value);
        if (results[2].status === "fulfilled") setSummary(results[2].value);
        setLoading(false);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        fetchData();
    }, []);

    const fetchProjectTasks = async (projectId) => {
        if (!projectId) {
            setTasks([]);
            return;
        }
        try {
            const tasksRes = await get(`/projects/${projectId}/tasks`);
            setTasks(tasksRes);
        } catch (error) {
            console.error("Error fetching tasks:", error);
        }
    };

    const handleProjectChange = (projectId) => {
        setSelectedProject(projectId);
        setFormData({ ...formData, task_id: "" });
        fetchProjectTasks(projectId);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.task_id || !formData.hours) {
            toast.error("Veuillez sélectionner une tâche et entrer les heures");
            return;
        }

        setSubmitting(true);
        try {
            await post("/time-entries", {
                ...formData,
                hours: parseFloat(formData.hours),
            });
            toast.success("Temps enregistré avec succès");
            setDialogOpen(false);
            setFormData({ task_id: "", description: "", hours: "", date: new Date() });
            setSelectedProject("");
            fetchData();
        } catch (error) {
            toast.error("Erreur lors de l'enregistrement");
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (entryId) => {
        if (!window.confirm("Supprimer cette entrée ?")) return;
        try {
            await del(`/time-entries/${entryId}`);
            toast.success("Entrée supprimée");
            fetchData();
        } catch (error) {
            toast.error("Erreur lors de la suppression");
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="time-tracking-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-heading font-bold text-2xl sm:text-3xl text-foreground">Suivi du temps</h1>
                    <p className="text-muted-foreground mt-1">
                        {summary?.total_hours?.toFixed(1) || 0} heures enregistrées au total
                    </p>
                </div>

                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogTrigger asChild>
                        <Button data-testid="add-time-entry-btn">
                            <Plus className="w-4 h-4 mr-2" />
                            Enregistrer du temps
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md">
                        <DialogHeader>
                            <DialogTitle className="font-heading">Enregistrer du temps</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <Label>Projet *</Label>
                                <Select value={selectedProject} onValueChange={handleProjectChange}>
                                    <SelectTrigger data-testid="time-project-select">
                                        <SelectValue placeholder="Sélectionner un projet" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {projects.map((project) => (
                                            <SelectItem key={project.project_id} value={project.project_id}>
                                                {project.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div>
                                <Label>Tâche *</Label>
                                <Select
                                    value={formData.task_id}
                                    onValueChange={(value) => setFormData({ ...formData, task_id: value })}
                                    disabled={!selectedProject}
                                >
                                    <SelectTrigger data-testid="time-task-select">
                                        <SelectValue placeholder={selectedProject ? "Sélectionner une tâche" : "Sélectionnez d'abord un projet"} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {tasks.map((task) => (
                                            <SelectItem key={task.task_id} value={task.task_id}>
                                                {task.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label>Heures *</Label>
                                    <Input
                                        type="number"
                                        step="0.25"
                                        min="0.25"
                                        value={formData.hours}
                                        onChange={(e) => setFormData({ ...formData, hours: e.target.value })}
                                        placeholder="2.5"
                                        data-testid="time-hours-input"
                                    />
                                </div>

                                <div>
                                    <Label>Date</Label>
                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <Button variant="outline" className="w-full justify-start">
                                                <CalendarIcon className="mr-2 h-4 w-4" />
                                                {format(formData.date, "dd/MM/yyyy")}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0">
                                            <Calendar
                                                mode="single"
                                                selected={formData.date}
                                                onSelect={(date) => setFormData({ ...formData, date: date || new Date() })}
                                            />
                                        </PopoverContent>
                                    </Popover>
                                </div>
                            </div>

                            <div>
                                <Label>Description (optionnel)</Label>
                                <Textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    placeholder="Ce que vous avez fait..."
                                    rows={2}
                                />
                            </div>

                            <div className="flex justify-end gap-2">
                                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                                    Annuler
                                </Button>
                                <Button type="submit" disabled={submitting} data-testid="submit-time-entry-btn">
                                    {submitting ? "Enregistrement..." : "Enregistrer"}
                                </Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card>
                    <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                            <div className="p-3 rounded-xl bg-primary/10">
                                <Clock className="w-6 h-6 text-primary" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Total heures</p>
                                <p className="text-3xl font-bold font-mono">{summary?.total_hours?.toFixed(1) || 0}h</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                            <div className="p-3 rounded-xl bg-green-100 dark:bg-green-900/30">
                                <BarChart3 className="w-6 h-6 text-green-600" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Projets actifs</p>
                                <p className="text-3xl font-bold font-mono">{summary?.by_project?.length || 0}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                            <div className="p-3 rounded-xl bg-blue-100 dark:bg-blue-900/30">
                                <CalendarIcon className="w-6 h-6 text-blue-600" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Entrées</p>
                                <p className="text-3xl font-bold font-mono">{summary?.entries_count || 0}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Chart & Recent Entries */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Hours by Project Chart */}
                <Card>
                    <CardHeader>
                        <CardTitle className="font-heading text-lg">Heures par projet</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {summary?.by_project?.length > 0 ? (
                            <div className="h-64">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={summary.by_project.slice(0, 5)} layout="vertical">
                                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                        <XAxis type="number" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                                        <YAxis
                                            type="category"
                                            dataKey="project_name"
                                            tick={{ fontSize: 12 }}
                                            width={120}
                                            stroke="hsl(var(--muted-foreground))"
                                        />
                                        <Tooltip
                                            contentStyle={{
                                                backgroundColor: "hsl(var(--card))",
                                                border: "1px solid hsl(var(--border))",
                                                borderRadius: "8px",
                                            }}
                                            formatter={(value) => [`${value}h`, "Heures"]}
                                        />
                                        <Bar dataKey="hours" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        ) : (
                            <div className="h-64 flex items-center justify-center text-muted-foreground">
                                Aucune donnée disponible
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Recent Entries */}
                <Card>
                    <CardHeader>
                        <CardTitle className="font-heading text-lg">Entrées récentes</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {entries.length === 0 ? (
                            <div className="text-center py-8 text-muted-foreground">
                                <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                <p>Aucune entrée de temps</p>
                            </div>
                        ) : (
                            <div className="space-y-3 max-h-64 overflow-y-auto">
                                {entries.slice(0, 10).map((entry) => {
                                    const project = projects.find(p => p.project_id === entry.project_id);
                                    return (
                                        <div
                                            key={entry.entry_id}
                                            className="flex items-center justify-between p-3 rounded-lg bg-secondary/50"
                                            data-testid={`time-entry-${entry.entry_id}`}
                                        >
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <Badge variant="outline" className="text-xs">
                                                        {entry.hours}h
                                                    </Badge>
                                                    <span className="text-sm font-medium truncate">
                                                        {project?.name || "Projet"}
                                                    </span>
                                                </div>
                                                {entry.description && (
                                                    <p className="text-xs text-muted-foreground mt-1 truncate">
                                                        {entry.description}
                                                    </p>
                                                )}
                                                <p className="text-xs text-muted-foreground mt-1">
                                                    {formatDate(entry.date)}
                                                </p>
                                            </div>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8"
                                                onClick={() => handleDelete(entry.entry_id)}
                                            >
                                                <Trash2 className="w-4 h-4 text-muted-foreground" />
                                            </Button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};
